import React, { Children, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import { Box, List, ListItem, ListItemIcon, ListItemText, Popover, Skeleton, Typography, useTheme } from '@mui/material'
import { ReactComponent as RentalFeeIcon } from 'assets/RentalFee.svg'

import { get } from 'utils/lodash'
import { Z_INDEX } from 'theme/zIndex'
import { handleError } from 'utils/error'
import { useLazyGetWorkOrderDetailsQuery } from 'api/work-order/getWorkOrderDetails'
import { HHAlert } from 'components/common/HHAlert'
import { generalFeeDefaultFilter } from 'components/work-order/details/invoice-details/filters'
import { useGetGeneralFeeListMutation } from 'api/pricing/getGeneralFeeList'

import HHDisplayMoney from 'components/common/HHDisplayMoney'
import { getFees, getFeeAndCharges, getFeesMapById } from './utils'

const GeneralFeesPopover = ({ anchorEl, workOrderId, onClose }) => {
  const theme = useTheme()
  const [getGeneralFeeList, { data: allFees, isLoading: isGeneralFeesLoading }] = useGetGeneralFeeListMutation()
  const [getWorkOrderDetails, { data: workOrder, isFetching: isWOLoading }] = useLazyGetWorkOrderDetailsQuery()

  const isOpen = Boolean(anchorEl)
  const isLoading = useMemo(() => isGeneralFeesLoading || isWOLoading, [isGeneralFeesLoading, isWOLoading])
  const feeAndCharges = useMemo(() => getFeeAndCharges(allFees), [allFees])
  const feesMapById = useMemo(() => getFeesMapById(feeAndCharges), [feeAndCharges])
  const addedFees = useMemo(() => getFees(workOrder), [workOrder])
  const hasAddedFees = useMemo(() => addedFees.length > 0, [addedFees])

  useEffect(() => {
    if (isOpen && workOrderId) {
      getGeneralFeeList(generalFeeDefaultFilter).unwrap().catch(handleError)
      getWorkOrderDetails({ workOrderId }).unwrap().catch(handleError)
    }
  }, [isOpen, workOrderId])

  return (
    <>
      <Popover
        sx={{ pointerEvents: 'none', zIndex: Z_INDEX.BADGE_POPOVER }}
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={onClose}
        disableRestoreFocus
        slotProps={{ paper: { sx: { minWidth: 350 } } }}
      >
        <List>
          {isLoading && (
            <ListItem>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 2 }}>
                <Skeleton variant="rectangular" width="1.5rem" height="1rem" />
              </ListItemIcon>
              <ListItemText primary={<Skeleton variant="text" width={100} />} secondary={<Skeleton variant="text" width={50} />} />
              <ListItemIcon>
                <Skeleton variant="text" width={50} />
              </ListItemIcon>
            </ListItem>
          )}

          {!isLoading && !hasAddedFees && (
            <Box sx={{ p: 2 }}>
              <HHAlert borderColor={theme.palette.info.light} severity="info">
                No general fees
              </HHAlert>
            </Box>
          )}

          {!isLoading &&
            hasAddedFees &&
            Children.toArray(
              addedFees.map(fee => {
                const { feeTaxId, quantity = 0, feeChargeCents = 0 } = fee
                return (
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                      <RentalFeeIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={get(feesMapById[feeTaxId], 'feeName', '')}
                      secondary={
                        <>
                          {`${quantity} x `}
                          <HHDisplayMoney value={feeChargeCents} />
                        </>
                      }
                      slotProps={{
                        primary: { variant: 'h6', fontWeight: 400, noWrap: true },
                        secondary: { variant: 'caption' },
                      }}
                    />
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      <Typography variant="h5" fontWeight={500} color="textPrimary">
                        <HHDisplayMoney value={quantity * feeChargeCents} />
                      </Typography>
                    </ListItemIcon>
                  </ListItem>
                )
              })
            )}
        </List>
      </Popover>
    </>
  )
}

GeneralFeesPopover.propTypes = {
  anchorEl: PropTypes.object,
  workOrderId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default GeneralFeesPopover
